import { useState, useCallback } from "react";
import DOMPurify from "dompurify";
import { FiMessageSquare, FiX } from "react-icons/fi";
import type { PanelMemberScore } from "../../types/meeting.type";

interface ScoreCellProps {
  score: PanelMemberScore;
}

export const ScoreCell: React.FC<ScoreCellProps> = ({ score }) => {
  const [showComment, setShowComment] = useState(false);

  const hasComment = !!score.final_comment;
  const sanitizedComment = hasComment
    ? DOMPurify.sanitize(score.final_comment!)
    : "";

  const handleOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setShowComment(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowComment(false);
  }, []);

  if (score.is_revoked) {
    return (
      <div className="text-center text-xs text-gray-400 italic">Revoked</div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="text-center font-medium text-sm">
        {score.final_score !== null
          ? score.final_score
          : <span className="text-gray-400">&mdash;</span>}
      </div>
      {score.final_weighted_score !== null && (
        <div className="text-center text-xs text-gray-400">
          {score.final_weighted_score.toFixed(1)}w
        </div>
      )}
      {hasComment && (
        <button
          onClick={handleOpen}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors mx-auto"
          title="View comment"
        >
          <FiMessageSquare className="w-3 h-3" />
        </button>
      )}

      {/* Comment modal overlay */}
      {showComment && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30"
          onClick={handleClose}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900">
                {score.member_name ?? "Panel Member"} — Comment
              </h4>
              <button
                onClick={handleClose}
                className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
            <div
              className="px-4 py-3 text-sm text-gray-700 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizedComment }}
            />
            <div className="flex justify-end px-4 py-3 border-t border-gray-100">
              <button
                onClick={handleClose}
                className="rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
